import type {
  AcceptInviteInput,
  ClubBillingDto,
  ClubBillingInput,
  ClubDto,
  ClubUserDto,
  CreateClubInput,
  InviteClubUserInput,
  InviteInfoDto,
  ResetPasswordInput,
  RoleDto,
  UpdateClubInput,
  UpdateUserInput,
} from "@tc/shared";

const API_URL = import.meta.env.VITE_API_URL ?? "/api";

/** A failed API call, carrying the server's message + HTTP status for the UI to surface. */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, {
    headers: { accept: "application/json", "content-type": "application/json" },
    ...init,
  });
  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    try {
      const body = (await res.json()) as { message?: string | string[] };
      if (body?.message)
        message = Array.isArray(body.message) ? body.message.join(", ") : body.message;
    } catch {
      /* non-JSON error body — keep the status message */
    }
    throw new ApiError(message, res.status);
  }
  return (res.status === 204 ? undefined : await res.json()) as T;
}

/** Back-office club & user administration calls (TW-3436). Plain fetch — no TanStack yet. */
export const adminApi = {
  // Clubs
  listClubs: () => req<ClubDto[]>("/clubs"),
  createClub: (body: CreateClubInput) =>
    req<ClubDto>("/clubs", { method: "POST", body: JSON.stringify(body) }),
  updateClub: (id: string, body: UpdateClubInput) =>
    req<ClubDto>(`/clubs/${id}`, { method: "PATCH", body: JSON.stringify(body) }),

  // Club billing details (TW-3527) — back-office view/edit of any club's billing, by club id.
  getClubBilling: (clubId: string) => req<ClubBillingDto>(`/clubs/${clubId}/billing`),
  updateClubBilling: (clubId: string, body: ClubBillingInput) =>
    req<ClubBillingDto>(`/clubs/${clubId}/billing`, { method: "PUT", body: JSON.stringify(body) }),

  // Users (per club)
  listRoles: () => req<RoleDto[]>("/roles"),
  listClubUsers: (clubId: string) => req<ClubUserDto[]>(`/clubs/${clubId}/users`),
  inviteUser: (clubId: string, body: InviteClubUserInput) =>
    req<ClubUserDto>(`/clubs/${clubId}/users`, { method: "POST", body: JSON.stringify(body) }),
  resendInvite: (clubId: string, userId: string) =>
    req<ClubUserDto>(`/clubs/${clubId}/users/${userId}/resend`, { method: "POST" }),
  updateUser: (id: string, body: UpdateUserInput) =>
    req<ClubUserDto>(`/users/${id}`, { method: "PATCH", body: JSON.stringify(body) }),

  // Public invite flow
  getInvite: (token: string) => req<InviteInfoDto>(`/invite/${token}`),
  acceptInvite: (token: string, body: AcceptInviteInput) =>
    req<{ ok: true }>(`/invite/${token}/accept`, { method: "POST", body: JSON.stringify(body) }),

  // Public forgot/reset-password flow. requestPasswordReset is enumeration-safe (always 200);
  // validateResetToken checks a link before showing the form; resetPassword sets the new password.
  requestPasswordReset: (email: string) =>
    req<{ ok: true }>("/password-reset", { method: "POST", body: JSON.stringify({ email }) }),
  validateResetToken: (token: string) => req<{ ok: true }>(`/password-reset/${token}`),
  resetPassword: (token: string, body: ResetPasswordInput) =>
    req<{ ok: true }>(`/password-reset/${token}`, { method: "POST", body: JSON.stringify(body) }),
};
